// src/scenes/meal-entry/MealCountsMonthly.jsx
import React, { useState, useEffect } from "react";
import {
    Box, Table, TableHead, TableRow, TableCell, TableBody,
    TextField, Select, MenuItem, FormControl, InputLabel,
    IconButton, Button, Typography, useTheme,
    CircularProgress, Modal
} from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

/* ---------- constants ---------- */
const blankCounts = {
    BR_Free: 0, BR_Reduced: 0, BR_Paid: 0,
    LN_Free: 0, LN_Reduced: 0, LN_Paid: 0,
    SP_Free: 0, SP_Reduced: 0, SP_Paid: 0,
    SN_Free: 0, SN_Reduced: 0, SN_Paid: 0,
};
const mealPrefixMap = { BR: "Breakfast", LN: "Lunch", SP: "Supper", SN: "Snack" };
const makeRow = (sch) => ({
    ...blankCounts,
    tempId: crypto.randomUUID(),
    siteId: sch?.id || "",
    siteName: sch?.name || "",
    isHighRate: sch?.isHighRate || false,
    date: "",
});

export default function MealCountsEntryMonthly() {
    const theme = useTheme();
    const { palette: { divider, action, background, text, primary, success, warning, error: errColor, info } } = theme;

    /* ---------- auth ---------- */
    const { allocations, isSchoolUser } = useAuth();
    const allowedIds = allocations.map(a => a.SchoolId);

    /* ---------- selectors ---------- */
    const [globalCategory, setGlobalCategory] = useState(8);
    const [globalSubcategory, setGlobalSubcategory] = useState(57);

    /* ---------- look-ups ---------- */
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [schools, setSchools] = useState([]);        // {id,name,isHighRate}
    const [rates, setRates] = useState({});        // { Breakfast:{Base:{},High:{}}, � }

    /* ---------- UI state ---------- */
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState("");
    const [error, setError] = useState("");

    /* ---------- progress ---------- */
    const [progressOpen, setProgressOpen] = useState(false);
    const [progressMessage, setProgressMessage] = useState("");

    /* ---------- fetch look-ups ---------- */
    useEffect(() => {
        Promise.all([
            axios.get("https://figazuresql.swancreate.com/api/swa/SchoolMst"),
            axios.get("https://figazuresql.swancreate.com/api/swa/finance_categories"),
            axios.get("https://figazuresql.swancreate.com/api/swa/finance_subcategories"),
            axios.get("https://figazuresql.swancreate.com/api/swa/MealReimbursementRates"),
        ])
            .then(([schRes, catRes, subRes, rateRes]) => {
                /* schools (keep isHighRate bit) */
                let list = schRes.data
                    .filter(s => s.SchoolId && s.SchoolName)
                    .map(s => ({
                        id: s.SchoolId,
                        name: s.SchoolName,
                        isHighRate: Boolean(Number(s.isHighRate ?? s.IsHighRate ?? 0)),
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));
                if (isSchoolUser) list = list.filter(s => allowedIds.includes(s.id));
                setSchools(list);

                /* finance categories/subcats */
                setCategories(catRes.data.filter(c => c.Type === "Income"));
                setSubcategories(subRes.data);

                /* rates: Base vs High */
                const grouped = {};
                rateRes.data.forEach(r => {
                    if (!grouped[r.MealService]) grouped[r.MealService] = { Base: {}, High: {} };
                    const st = r.PaymentStatus;             // Free / Reduced / Paid
                    grouped[r.MealService].Base[st] = parseFloat(r.Rate ?? 0);
                    grouped[r.MealService].High[st] = parseFloat(r.HighRate ?? r.RateHigh ?? r.Rate ?? 0);
                });
                setRates(grouped);
            })
            .catch(err => { console.error(err); setFetchError("Lookup load failed"); })
            .finally(() => setLoading(false));
    }, [allocations, isSchoolUser]);

    /* ---------- start with 1 row when schools ready ---------- */
    useEffect(() => { if (schools.length) setRows([makeRow(schools[0])]); }, [schools]);

    /* ---------- helpers ---------- */
    const handleCellChange = (idx, field, value) => {
        setRows(rs => rs.map((r, i) => {
            if (i !== idx) return r;
            if (field === "siteId") {
                const sel = schools.find(s => s.id === value) || {};
                return { ...r, siteId: value, siteName: sel.name || "", isHighRate: sel.isHighRate };
            }
            return { ...r, [field]: field === "date" ? value : Number(value) || 0 };
        }));
    };

    const mealTotalMoney = (row, prefix) => {
        const tier = row.isHighRate ? "High" : "Base";
        const svc = (rates[mealPrefixMap[prefix]] || {})[tier] || {};
        return (
            row[`${prefix}_Free`] * (svc.Free || 0) +
            row[`${prefix}_Reduced`] * (svc.Reduced || 0) +
            row[`${prefix}_Paid`] * (svc.Paid || 0)
        );
    };
    const rowTotalMoney = r =>
        Object.keys(mealPrefixMap).reduce((s, p) => s + mealTotalMoney(r, p), 0);

    const addRow = () => setRows(rs => [...rs, makeRow(schools[0])]);
    const removeRow = idx => setRows(rs => rs.filter((_, i) => i !== idx));

    /* ---------- submit (unchanged) ---------- */
    const handleSubmit = async () => {
        if (!globalCategory) return setError("Select a Category.");
        if (!globalSubcategory) return setError("Select a Subcategory.");
        for (const r of rows) if (!r.siteId || !r.date) return setError("Each row needs Site and Date.");
        setError(""); setProgressOpen(true);

        try {
            /* meal counts */
            for (let i = 0; i < rows.length; i++) {
                const r = rows[i];
                setProgressMessage(`Saving meal counts for ${r.siteName}�`);
                await axios.post("https://figazuresql.swancreate.com/api/swa/FINANCE_MealCounts", {
                    MonthDate: r.date, Date: r.date, SchoolId: r.siteId, SchoolName: r.siteName,
                    ...Object.fromEntries(Object.keys(blankCounts).map(k => [k, r[k]])),
                    BR_Total: mealTotalMoney(r, "BR"), LN_Total: mealTotalMoney(r, "LN"),
                    SP_Total: mealTotalMoney(r, "SP"), SN_Total: mealTotalMoney(r, "SN"),
                    Summary_Total: rowTotalMoney(r),
                });
            }
            /* incomes */
            const catName = categories.find(c => c.CategoryID === globalCategory)?.CategoryName || "";
            const subName = subcategories.find(sc => sc.SubcategoryID === globalSubcategory)?.SubcategoryName || "";
            for (let i = 0; i < rows.length; i++) {
                const r = rows[i];
                setProgressMessage(`Saving income for ${r.siteName}�`);
                await axios.post("https://figazuresql.swancreate.com/api/swa/FINANCE_Incomes", {
                    MonthDate: r.date, IncomeDate: r.date, SchoolId: r.siteId, SiteName: r.siteName,
                    IncomeCategoryID: globalCategory, IncomeCategory: catName,
                    IncomeSubCategoryID: globalSubcategory, IncomeSubcategory: subName,
                    Amount: rowTotalMoney(r), Description: "",
                });
            }
            /* reset */
            setRows([makeRow(schools[0])]);
        } catch (e) { console.error(e); setError("Save failed"); }
        finally { setProgressOpen(false); }
    };

    /* ---------- quick exits ---------- */
    if (loading) return <CircularProgress />;
    if (fetchError) return <Typography color="error">{fetchError}</Typography>;

    const evenBg = theme.palette.mode === "light" ? action.hover : primary[600];
    const oddBg = theme.palette.mode === "light" ? background.paper : primary[700];

    /* ---------- render ---------- */
    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>FIG Monthly Meal Counts by Site</Typography>

            {/* selectors */}
            <Box display="flex" gap={2} mb={3}>
                <FormControl size="small">
                    <InputLabel>Category</InputLabel>
                    <Select value={globalCategory} onChange={e => setGlobalCategory(e.target.value)} label="Category">
                        {categories.map(c => <MenuItem key={c.CategoryID} value={c.CategoryID}>{c.CategoryName}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small">
                    <InputLabel>Subcategory</InputLabel>
                    <Select value={globalSubcategory} onChange={e => setGlobalSubcategory(e.target.value)} label="Subcategory">
                        {subcategories.filter(sc => sc.CategoryID === globalCategory)
                            .map(sc => <MenuItem key={sc.SubcategoryID} value={sc.SubcategoryID}>{sc.SubcategoryName}</MenuItem>)}
                    </Select>
                </FormControl>
            </Box>

            {/* table */}
            <Table size="small" sx={{ border: `1px solid ${divider}` }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ backgroundColor: primary.main, color: "#fff" }}>Site</TableCell>
                        <TableCell sx={{ backgroundColor: primary.main, color: "#fff" }}>Date</TableCell>
                        {Object.entries(mealPrefixMap).map(([p, m]) => (
                            <React.Fragment key={p}>
                                <TableCell align="center" sx={{
                                    backgroundColor: m === "Breakfast" ? success.main : m === "Lunch" ? info.main :
                                        m === "Supper" ? warning.main : errColor.main, color: "#fff"
                                }}>
                                    {m}<br /><Typography variant="caption">F&nbsp;R&nbsp;P</Typography>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    backgroundColor: m === "Breakfast" ? success.main : m === "Lunch" ? info.main :
                                        m === "Supper" ? warning.main : errColor.main, color: "#fff"
                                }}>$</TableCell>
                            </React.Fragment>
                        ))}
                        <TableCell sx={{ backgroundColor: primary.main, color: "#fff" }}>Total $</TableCell>
                        <TableCell sx={{ backgroundColor: primary.main }} />
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map((r, i) => (
                        <TableRow key={r.tempId} sx={{ backgroundColor: i % 2 ? evenBg : oddBg }}>
                            {/* site */}
                            <TableCell>
                                <FormControl fullWidth size="small">
                                    <Select value={r.siteId} size="small"
                                        onChange={e => handleCellChange(i, "siteId", e.target.value)}>
                                        {schools.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            {/* date */}
                            <TableCell>
                                <TextField type="date" size="small" fullWidth
                                    value={r.date} onChange={e => handleCellChange(i, "date", e.target.value)} />
                            </TableCell>
                            {/* meal counts */}
                            {Object.keys(mealPrefixMap).map(pref => (
                                <React.Fragment key={pref}>
                                    <TableCell>
                                        <Box display="flex" gap={1}>
                                            {["Free", "Reduced", "Paid"].map(stat => (
                                                <TextField key={stat} type="number" size="small" sx={{
                                                    width: 60, "& input[type=number]": { MozAppearance: "textfield" },
                                                    "& input::-webkit-outer-spin-button,& input::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 }
                                                }}
                                                    value={r[`${pref}_${stat}`]}
                                                    onChange={e => handleCellChange(i, `${pref}_${stat}`, e.target.value)}
                                                />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        {mealTotalMoney(r, pref).toLocaleString(undefined, { style: "currency", currency: "USD" })}
                                    </TableCell>
                                </React.Fragment>
                            ))}
                            <TableCell>
                                {rowTotalMoney(r).toLocaleString(undefined, { style: "currency", currency: "USD" })}
                            </TableCell>
                            <TableCell>
                                <IconButton onClick={() => removeRow(i)}><RemoveCircleOutline /></IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                    {/* add row */}
                    <TableRow>
                        <TableCell colSpan={Object.keys(mealPrefixMap).length * 2 + 3} align="center">
                            <IconButton onClick={addRow}><AddCircleOutline /> Add Row</IconButton>
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableBody>
            </Table>

            {error && <Typography color="error" mt={2}>{error}</Typography>}

            {/* submit & summary */}
            <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                <Button variant="contained" onClick={handleSubmit}>Submit</Button>
                <Typography>
                    Summary Total:&nbsp;
                    <Typography component="span" color="text.secondary" fontWeight="bold">
                        {rows.reduce((s, r) => s + rowTotalMoney(r), 0).toLocaleString(undefined, { style: "currency", currency: "USD" })}
                    </Typography>
                </Typography>
            </Box>

            {/* progress */}
            <Modal open={progressOpen} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Box sx={{ backgroundColor: background.paper, p: 3, borderRadius: 2, boxShadow: 24, textAlign: "center" }}>
                    <CircularProgress />
                    <Typography mt={2}>{progressMessage || "Processing�"}</Typography>
                </Box>
            </Modal>
        </Box>
    );
}
